import type { Book, DownloadFormat } from '../../shared/types'

interface ApiError {
  error?: string
}

export interface DownloadRequest {
  bookId: number
  format: DownloadFormat
  name: string
  email: string
  phone: string
  isBusinessOwner: boolean
  optInNewsletter: boolean
  consent: boolean
}

async function getErrorMessage(response: Response, fallback: string) {
  try {
    const data = (await response.json()) as ApiError
    return data.error ?? fallback
  } catch {
    return fallback
  }
}

export async function getCatalogue(): Promise<Book[]> {
  const response = await fetch('/api/catalog')
  if (!response.ok) {
    throw new Error(await getErrorMessage(response, 'Não foi possível carregar as obras.'))
  }
  return (await response.json()) as Book[]
}

export async function requestDownload(payload: DownloadRequest) {
  const response = await fetch('/api/downloads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, 'Não foi possível iniciar o download.'))
  }

  const disposition = response.headers.get('Content-Disposition') ?? ''
  const filename = /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? 'ebook'
  return { blob: await response.blob(), filename }
}

export interface AdminSession {
  authenticated: boolean
  username?: string
}

export interface DownloadRecord {
  id: number
  name: string
  email: string
  phone: string
  isBusinessOwner: boolean
  optInNewsletter: boolean
  bookId?: number
  bookTitle: string
  format: DownloadFormat
  downloadedAt: string
}

export interface DashboardData {
  period: string
  startDate?: string | null
  endDate?: string | null
  generatedAt: string
  summary: {
    totalDownloads: number
    uniqueReaders: number
    pdfDownloads: number
    epubDownloads: number
    businessOwners: number
    newsletterOptIns: number
  }
  byBook: Array<{ id: number; title: string; downloads: number }>
  recent: DownloadRecord[]
  downloads: DownloadRecord[]
}

export async function getAdminSession(): Promise<AdminSession> {
  const response = await fetch('/api/admin/session', { credentials: 'include' })
  if (response.status === 401) return { authenticated: false }
  if (!response.ok) throw new Error(await getErrorMessage(response, 'Não foi possível verificar a sessão.'))
  return (await response.json()) as AdminSession
}

export async function login(username: string, password: string): Promise<AdminSession> {
  const response = await fetch('/api/admin/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  if (!response.ok) throw new Error(await getErrorMessage(response, 'Credenciais inválidas.'))
  return (await response.json()) as AdminSession
}

export async function logout() {
  await fetch('/api/admin/logout', { method: 'POST', credentials: 'include' })
}

export async function getDashboard(
  period: string,
  startDate?: string,
  endDate?: string,
): Promise<DashboardData> {
  const params = new URLSearchParams()
  if (period) params.set('period', period)
  if (startDate) params.set('startDate', startDate)
  if (endDate) params.set('endDate', endDate)

  const response = await fetch(`/api/admin/dashboard?${params.toString()}`, {
    credentials: 'include',
  })
  if (response.status === 401) throw new Error('Sua sessão expirou. Entre novamente.')
  if (!response.ok) throw new Error(await getErrorMessage(response, 'Não foi possível carregar os relatórios.'))
  return (await response.json()) as DashboardData
}
